import {Box, FormControl, Typography} from '@mui/material';
import {RichTreeView} from '@mui/x-tree-view/RichTreeView';
import {memo, useEffect, useState} from 'react';
import {ModuleTopic} from '@cp/model';
import TopicSelectionHeader from './TopicSelectionHeader';
import useTopicSelection from './useTopicSelection';

// Memoized RichTreeView component to prevent unnecessary re-renders
const MemoizedRichTreeView = memo(RichTreeView);

export interface TopicSelectionProps {
  moduleTopics: ModuleTopic[];
  /** topic only without category */
  selectedTopics: string[];
  /** update function with topics only without category */
  onSelectionChange: (topics: string[]) => void;
  height?: number;
  showSelectAll?: boolean;
  disabled?: boolean;
}

function TopicSelection({
  moduleTopics,
  selectedTopics,
  onSelectionChange,
  height = 400,
  showSelectAll = true,
  disabled = false,
}: TopicSelectionProps) {
  const [shouldRenderTree, setShouldRenderTree] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const {
    treeObject,
    allParentTopicStrings,
    allTopicStrings,
    internalSelectedTopics,
    handleToggleAll,
    handleTreeSelectionChange,
  } = useTopicSelection({moduleTopics, selectedTopicsOnly: selectedTopics, onSelectionChange, disabled});

  // Defer RichTreeView rendering to avoid blocking accordion expansion
  useEffect(() => {
    // Delay tree rendering slightly to let accordion animation complete
    // Start collapsed (empty expandedItems) so only top-level categories render initially
    // User can expand categories manually - much faster initial render!
    const renderDelay = setTimeout(() => {
      setShouldRenderTree(true);
      // Don't auto-expand - let user expand categories manually for better performance
      // This means only 6 top-level items render initially instead of 98+ items
    }, 50); // Small delay to let accordion open animation start

    return () => clearTimeout(renderDelay);
  }, [allParentTopicStrings]);

  // Checkbox state calculations
  const isAllSelected = allTopicStrings.length > 0 && internalSelectedTopics.length === allTopicStrings.length;
  const isIndeterminate = internalSelectedTopics.length > 0 && internalSelectedTopics.length < allTopicStrings.length;

  const handleExpandedItemsChange = (_event: React.SyntheticEvent | null, itemIds: string[]) => {
    setExpandedItems(itemIds);
  };

  return (
    <FormControl fullWidth>
      <Box>
        <TopicSelectionHeader
          showSelectAll={showSelectAll}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
          disabled={disabled}
          onToggleAll={handleToggleAll}
        />
        <Box
          sx={{
            height,
            minWidth: 250,
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {shouldRenderTree ? (
            <MemoizedRichTreeView
              items={treeObject}
              checkboxSelection={!disabled}
              multiSelect
              selectionPropagation={{parents: true, descendants: true}}
              selectedItems={internalSelectedTopics}
              onSelectedItemsChange={disabled ? undefined : handleTreeSelectionChange}
              expandedItems={expandedItems}
              onExpandedItemsChange={handleExpandedItemsChange}
            />
          ) : (
            <Box sx={{p: 2, textAlign: 'center'}}>
              <Typography variant="body2" color="text.secondary">
                Loading topics...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </FormControl>
  );
}

// Memoize the component to prevent unnecessary re-renders
// Only re-render when props actually change
export default memo(
  TopicSelection,
  (prevProps, nextProps) =>
    // Custom comparison function for better performance
    prevProps.moduleTopics === nextProps.moduleTopics &&
    prevProps.selectedTopics === nextProps.selectedTopics &&
    prevProps.onSelectionChange === nextProps.onSelectionChange &&
    prevProps.height === nextProps.height &&
    prevProps.showSelectAll === nextProps.showSelectAll &&
    prevProps.disabled === nextProps.disabled,
);
